import { useCallback, useState } from "preact/hooks";
import useAxiosCallback from "../../hooks/useAxiosCallback";
import { getDefaultStateParams } from "./TransactionsUtils";
import moment from "moment";

export default function useTransactionsDownload() {
    const NUMBER_OF_PARALLEL_REQUESTS = 4;
    const [loadingState, setLoadingState] = useState({
        batchSize: 0,
        bufferSize: 0
    });
    const [isLoading, setIsLoading] = useState(false);

    const { batchSize, bufferSize } = { ...loadingState };
    const { fetch } = useAxiosCallback();
    const { fileDownloadParams: fs } = getDefaultStateParams();

    const fetchStatistics = useCallback(async (config) => {
        let stats = {};
        const queryParams = {
          date_from: moment(config?.params?.dateFrom * 1000).format("YYYY-MM-DD HH:mm:ss"),
          date_to: moment(config?.params?.dateTo * 1000).format("YYYY-MM-DD HH:mm:ss"),
        };
        const response = await fetch(
          "/user/flow/stats/summary",
          {
            method: "GET",
            params: queryParams,
          },
          (response) => {
            stats.general = response.data;
          }, (error) => {
            console.log(error.message);
          }
        );

        const getEsignFlowStats = await fetch(
          "/user/journal/sessions/group-by-flow-id/stats",
          {
            method: "GET",
            params: {
                ...queryParams,
                service: 'esign',
                action: 'sign',
            },
          },
          (response) => {
            stats.signings = response.data;
          }, (error) => {
            console.log(error.message);
          }
        );

        const getEsignSessionsStats = await fetch(
            "/user/journal/sessions/stats/summary",
            {
                method: "GET",
                params: {
                    ...queryParams,
                    service: 'esign',
                    action: 'sign'
                },
            },
            (response) => {
                stats.signings = { ...stats.signings, ...response.data };
            }, (error) => {
                console.log(error.message);
            }
        );

        return stats;
    });

    const buildBaseQueryParams = useCallback((config) => {
        const limit = fs.defaultDownloadBatchSize;

        const baseQueryParams = {
            limit,
            date_from: moment(config?.params?.dateFrom * 1000).format("YYYY-MM-DD HH:mm:ss"),
            date_to: moment(config?.params?.dateTo * 1000).format("YYYY-MM-DD HH:mm:ss"),
            sort: 'created_at:desc',
        };

        const searchFilterMap = {
            flow_id: 'flow_id',
            person_id: 'person_id',
            name: 'person_name',
        };

        const selectedFilterKey = config?.params?.selectedSearchFilter;
        if (searchFilterMap[selectedFilterKey]) {
            baseQueryParams[searchFilterMap[selectedFilterKey]] = config?.params?.searchQuery;
        }

        config?.params?.selectedFilters.forEach(([filter_type, value]) => {
            if (baseQueryParams[filter_type] !== undefined) {
                baseQueryParams[filter_type] += `,${value}`;
            } else {
                baseQueryParams[filter_type] = value;
            }
        });

        return baseQueryParams;
    }, [fs.defaultDownloadBatchSize]);

    const fetchAllData = useCallback(async (endpoint, params) => {
        const limit = fs.defaultDownloadBatchSize;
        let offset = fs.initialDownloadCursorOffset;
        let allData = [];
        let hasMoreData = true;
        let currentOffset = offset;

        while (hasMoreData) {
            const batchRequests = [];

            for (let i = 0; i < NUMBER_OF_PARALLEL_REQUESTS && hasMoreData; i++) {
                const batchOffset = currentOffset + (i * limit);

                batchRequests.push({
                    offset: batchOffset,
                    promise: fetch(endpoint, {
                        method: "GET",
                        params: {...params, offset: batchOffset}
                    }).catch(error => {
                        console.log(error.message);
                        return {data: []};
                    })
                });
            }

            const batchPromises = batchRequests.map(request => request.promise);
            const batchResponses = await Promise.all(batchPromises);

            let allBatchesEmpty = true;

            for (let i = 0; i < batchRequests.length; i++) {
                const batchData = batchResponses[i].data || [];
                allData = allData.concat(batchData);

                if (batchData.length > 0) {
                    allBatchesEmpty = false;

                    if (batchData.length < limit) {
                        hasMoreData = false;
                        break;
                    }
                } else {
                    hasMoreData = false;
                    break;
                }
            }

            if (allBatchesEmpty) {
                hasMoreData = false;
            }

            currentOffset += NUMBER_OF_PARALLEL_REQUESTS * limit;
        }

        return allData;
    }, [fs.defaultDownloadBatchSize, fs.initialDownloadCursorOffset]);

    const fetchFile = useCallback(async (config) => {
        const baseQueryParams = buildBaseQueryParams(config);

        setIsLoading(true);

        try {
            const [groupByFlowData, signSessionsData] = await Promise.all([
                fetchAllData("/user/journal/sessions/group-by-flow-id", baseQueryParams),
                fetchAllData("/user/journal/sessions", {
                    ...baseQueryParams,
                    service: 'esign',
                    action: 'sign'
                })
            ]);

            return {
                transactions: groupByFlowData,
                signing_sessions: signSessionsData
            };
        } catch (error) {
            console.error("Data fetch terminated with error:", error);
            return {
                transactions: [],
                sign_sessions: []
            };
        } finally {
            setIsLoading(false);
        }
    }, [buildBaseQueryParams, fetchAllData]);

    return { isLoading, batchSize, bufferSize, fetchFile, fetchStatistics };
}